import {
  DesiredSerialParentData,
  DesiredSerialParentDataResponse,
  LinkShipmentSearchData,
  LinkShipmentSearchResult,
  LiveSearchResponse,
  LocationNodeData,
  PartOrPartTypeSearchData,
  PartSearchData,
  PartTypeActionLiveSearchItem,
  SearchData,
  ShipmentSearchData,
  StructureMergeLocationNodeData,
  StructureMergeSearchData,
  SubLocationSearch,
} from '../@types';
import { httpClient, HttpMethod } from './http-client';
import {
  AdvancePartSearch,
  BatchRecieveBulkLiveSearchData,
  PartLiveSearchData,
  PartTypeBulkLiveSearchDataInsertionType,
  PartTypeLiveSearchData,
} from '../@types/part-type.type';
import { SearchNodeParts } from '../@types/part.type';
import { ServiceData } from '../@types/service-search.type';
import { RecieveShipmentSearchNodes, SerializedShipmentManifestData } from '../@types/shipment.type';
import { getPartTypeActionLiveSearchSerialiseType } from '../utils/part-type';

// Search data interfaces for functions that accept `any` data parameter
interface SearchNodeByParentIdData {
  name: string;
  selectedParentId?: string | number;
  hasCode?: boolean;
  includeParentNode?: boolean;
  includeSiblings?: boolean;
  excludeReferenceSearch?: boolean;
}

interface ManufacturerSearchData {
  name: string;
  applicableNodeIds: string | number[];
}

interface NodePartsSearchData {
  id: number;
  name: string;
  nodeIdTree: string;
  includeSiblings: boolean;
  applicableNodeIds?: string | number[];
}

interface ChildPartTypesSearchData {
  name: string;
  applicableNodeIds: string | number[];
  categoryCode?: string;
}

interface PartTypeSearchByNameData {
  name: string;
  applicableNodeIds?: string;
  nodeId?: number;
}

interface DispatchPartsSearchData {
  name: string;
  applicableNodeIds?: string;
}

interface TlcNodesPartsSearchData {
  id?: number;
  name: string;
  currentLocation: number;
  currentLocationType?: string;
}

export const searchNodeOperational = (name: string, hasCode?: boolean): Promise<LocationNodeData[]> => {
  let url = `/searchnodes?name=${encodeURIComponent(name)}&categoryCode=operational`;
  // hasCode property allow AM-LID search when true
  if (hasCode) {
    url += '&hasCode=true';
  }
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const getSubLocationsForCreateServices = (
  id: number,
  isNestedCheck?: boolean
): Promise<LocationNodeData[]> => {
  let url = `/subLocationsForCreateServices/${id}`;
  if (isNestedCheck) {
    url += `?isNestedCheck=${isNestedCheck}`;
  }
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchNode = (name: string): Promise<LocationNodeData[]> => {
  return httpClient<LocationNodeData[]>(`/searchnodes?name=${encodeURIComponent(name)}`, HttpMethod.Get);
};

export const searchNodeByParentId = (data: SearchNodeByParentIdData): Promise<LocationNodeData[]> => {
  let url = `/searchnodes?name=${encodeURIComponent(data?.name)}&categoryCode=operational`;

  if (data?.selectedParentId) {
    url += `&parentNodeId=${parseInt(String(data?.selectedParentId))}`;
  }

  // hasCode property allow AM-LID search when true
  if (data?.hasCode) {
    url += '&hasCode=true';
  }

  if (data?.includeParentNode) {
    url = `${url}&includeParentNode=${data?.includeParentNode}`;
  }

  if (data?.includeSiblings) {
    url = `${url}&includeSiblings=${data?.includeSiblings}`;
  }

  if (data?.excludeReferenceSearch) {
    url += '&excludeReferenceSearch=true';
  }

  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchService = (id: number | string): Promise<ServiceData[]> => {
  return httpClient<ServiceData[]>(`/searchservices?id=${id}`, HttpMethod.Get);
};

export const searchManufacturerNodes = (name: string): Promise<LocationNodeData[]> => {
  const url = `/searchnodes?name=${encodeURIComponent(name)}&categoryCode=operational&nodeTypeCode=supplier-repairer`;
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchManufacturerNodesByClientAndContract = (
  data: ManufacturerSearchData
): Promise<LocationNodeData[]> => {
  const nodeIds = Array.isArray(data.applicableNodeIds)
    ? data.applicableNodeIds.join(',')
    : data.applicableNodeIds;
  const url = `/searchPartTypes?name=${encodeURIComponent(data.name)}&nodeIds=${nodeIds}&isSerialised=true`;
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchNodesParts = (data: NodePartsSearchData): Promise<LocationNodeData[]> => {
  const nodeIds = data.applicableNodeIds
    ? (Array.isArray(data.applicableNodeIds) ? data.applicableNodeIds.join(',') : data.applicableNodeIds)
    : undefined;
  let url = `/searchParts?id=${data.id}&name=${encodeURIComponent(data.name)}&nodeIdTree=${data.nodeIdTree}&categoryCode=operational&includeSiblings=${data.includeSiblings}&isNodeAndPartSearch=true`;
  if (nodeIds) {
    url += `&applicableNodeIds=[${nodeIds}]`;
  }
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchChildPartTypesByClientAndContract = (
  data: ChildPartTypesSearchData,
  isSerialized?: boolean
): Promise<LocationNodeData[]> => {
  const nodeIds = Array.isArray(data.applicableNodeIds)
    ? data.applicableNodeIds.join(',')
    : data.applicableNodeIds;
  let url = `/searchPartTypes?name=${encodeURIComponent(data.name)}&nodeIds=${nodeIds}&isSerialised=${isSerialized}`;
  if (data?.categoryCode) {
    url += `&categoryCode=${encodeURIComponent(data?.categoryCode)}`;
  }
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchPartTypesByClientAndContractForSKU = (
  data: ManufacturerSearchData,
  isSerialized?: boolean
): Promise<LocationNodeData[]> => {
  const nodeIds = Array.isArray(data.applicableNodeIds)
    ? data.applicableNodeIds.join(',')
    : data.applicableNodeIds;
  const url = `/searchPartTypes?name=${encodeURIComponent(data.name)}&nodeIds=${nodeIds}&isSerialised=${isSerialized}&forSKU=true`;
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchClientContractPartTypes = (data: ManufacturerSearchData): Promise<LocationNodeData[]> => {
  const nodeIds = Array.isArray(data.applicableNodeIds)
    ? data.applicableNodeIds.join(',')
    : data.applicableNodeIds;
  const url = `/siteSchema/searchPartTypes?name=${encodeURIComponent(data.name)}&nodeIds=${nodeIds}`;
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchParts = (data: SearchData, searchLimit?: number): Promise<LocationNodeData[]> => {
  // isRaw property means name not include any hardcodes (s#, AM-LID-##, AM-PID-##, etc..)
  let url = `/searchParts?name=${encodeURIComponent(data.name)}&categoryCode=operational&isRaw=true`;
  if (searchLimit) {
    url += `&searchLimit=${searchLimit}`;
  }
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const replenishmentSearchNodes = (data: SearchData): Promise<LocationNodeData[]> => {
  // hasCode property means whether AM-LID- included or not
  const url = `/searchnodes?name=${encodeURIComponent(data.name)}&categoryCode=operational&hasCode=true`;
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

// search SKUs
export const replenishmentSkuSearch = (data: PartTypeSearchByNameData): Promise<LocationNodeData[]> => {
  const url = `/searchSkuConfigs?name=${encodeURIComponent(data.name)}&nodeId=${data.nodeId}`;
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const replenishmentPartTypeSearch = (data: PartTypeSearchByNameData): Promise<LocationNodeData[]> => {
  const url = `/searchPartTypes?name=${encodeURIComponent(data.name)}&nodeId=${data.nodeId}`;
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchNodeShipment = (name: string): Promise<LocationNodeData[]> => {
  return httpClient<LocationNodeData[]>(
    `/searchnodes?name=${encodeURIComponent(name)}&isPart=true&includeSiblings=true&hasCode=true`,
    HttpMethod.Get
  );
};

export const advanceSearchPartTypes = (
  data: PartTypeBulkLiveSearchDataInsertionType
): Promise<PartTypeLiveSearchData[]> => {
  // support raw search and AM-PTID- search
  const searchableData = Array.isArray(data.searchableValues)
    ? data.searchableValues.join(',')
    : (data.searchableValues ?? '');
  let url = `/advanceSearchPartTypes?searchableData=${encodeURIComponent(searchableData)}&applicableNodeId=${data.applicableNodeId}`;
  if (data.searchType) {
    url += `&searchType=${encodeURIComponent(data.searchType)}`;
  }
  return httpClient<PartTypeLiveSearchData[]>(url, HttpMethod.Get);
};

export const advanceSearchPartTypesForBulkPartUpdate = (
  data: PartTypeBulkLiveSearchDataInsertionType
): Promise<PartTypeLiveSearchData[]> => {
  const searchableData = Array.isArray(data.searchableValues)
    ? data.searchableValues.join(',')
    : (data.searchableValues ?? '');
  const url = `/advanceSearchPartTypesForBulkUpdate?searchableData=${encodeURIComponent(searchableData)}&isSerialised=true`;
  return httpClient<PartTypeLiveSearchData[]>(url, HttpMethod.Get);
};

export const advanceBulkSearchPartTypes = (
  data: PartTypeBulkLiveSearchDataInsertionType
): Promise<PartTypeLiveSearchData[]> => {
  return httpClient<PartTypeLiveSearchData[]>('/advanceBulkSearchPartTypes', HttpMethod.Post, data);
};

export const searchNodeDispatch = (data: SearchData): Promise<LocationNodeData[]> => {
  let url = `/searchnodes?name=${encodeURIComponent(data.name)}&categoryCode=operational&includeSiblings=true&isPart=true`;
  if (data?.hasCode) {
    // hasCode property allow AM-LID search when true
    url += '&hasCode=true';
  }
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchPartsBulkUpdate = (data: SearchData): Promise<LocationNodeData[]> => {
  // enables raw serial search, AM-PID search with visibility rule check
  const url = `/searchParts?name=${encodeURIComponent(data.name)}&categoryCode=operational`;
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchPartsWithPid = (data: SearchData): Promise<LocationNodeData[]> => {
  // trigger when part ID search only enabled (work AM-PID- code only)
  const url = `/searchParts?name=${encodeURIComponent(data.name)}&categoryCode=operational&isPart=true`;
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchPartsOrPartTypes = (data: PartOrPartTypeSearchData): Promise<PartTypeActionLiveSearchItem[]> => {
  // name -: [part type name || S# || AM-PTID || AM-PID]
  // serialiseType -: [empty || 'serialised' || 'non-serialised']
  // partTypeId -: [empty || number]
  // clientContractIds -: [clientId, contractId]
  // allowedPartLocationType -: ['node' || 'part']
  // searchType -: ['part-and-part-type' || 'part-type' || 'part']
  const serialiseType = getPartTypeActionLiveSearchSerialiseType(data.serialiseType);
  let url = `/partOrPartType?name=${encodeURIComponent(data.name)}&serialiseType=${encodeURIComponent(serialiseType)}&partTypeId=${data.partTypeId ?? ''}&clientContractIds=${encodeURIComponent(JSON.stringify(data.clientContractIds))}&locationId=${data.locationId ?? ''}&allowedPartLocationType=${encodeURIComponent(data.allowedPartLocationType ?? '')}&partActionType=${encodeURIComponent(data.partActionType ?? '')}&searchType=${encodeURIComponent(data.searchType ?? '')}`;

  if (data?.reportedSerialNumberPartId) {
    url += `&reportedSerialNumberPartId=${data?.reportedSerialNumberPartId}`;
  }

  if (data?.serviceTypeCode) {
    url += `&serviceTypeCode=${encodeURIComponent(data?.serviceTypeCode)}`;
  }

  if (data?.outPartTypeId) {
    url += `&outPartTypeId=${data?.outPartTypeId}`;
  }

  if (data?.serialisedOutPartId) {
    url += `&serialisedOutPartId=${data?.serialisedOutPartId}`;
  }

  if (data?.nsOutPartId) {
    url += `&nsOutPartId=${data?.nsOutPartId}`;
  }

  if (data?.preFill) {
    url += `&preFill=${data.preFill}`;
  }

  return httpClient<PartTypeActionLiveSearchItem[]>(url, HttpMethod.Get);
};

export const searchReturnPageSource = (name: string, isLiveSearch?: boolean): Promise<LocationNodeData[]> => {
  let url = `/returnShipmentSourceSearch?name=${encodeURIComponent(name)}`;
  if (isLiveSearch) {
    url += '&isLiveSearch=true';
  }
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchReturnPageDestination = (
  name: string,
  isLiveSearch?: boolean
): Promise<LocationNodeData[]> => {
  let url = `/returnShipmentDestinationSearch?name=${encodeURIComponent(name)}`;
  if (isLiveSearch) {
    url += '&isLiveSearch=true';
  }
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const searchNodeInStructureMerge = (data: StructureMergeSearchData): Promise<StructureMergeLocationNodeData[]> => {
  return httpClient<StructureMergeLocationNodeData[]>(
    `/structureMergeSearchNodes?name=${encodeURIComponent(data.name)}&nodeTypeCode=${encodeURIComponent(data.nodeTypeCode)}&currentNodeId=${data.mergeNodeId}`,
    HttpMethod.Get
  );
};

export const searchDispatchParts = (data: DispatchPartsSearchData): Promise<PartSearchData[]> => {
  // isRaw property means name include hardcodes (s#, AM-LID-##, AM-PID-##, etc..)
  let url = `/searchParts?name=${encodeURIComponent(data.name)}&categoryCode=operational&isRaw=false`;
  if (data.applicableNodeIds) {
    url += `&applicableNodeIds=${data.applicableNodeIds}`;
  }
  return httpClient<PartSearchData[]>(url, HttpMethod.Get);
};

export const advanceBulkSearchSerials = (
  data: PartTypeBulkLiveSearchDataInsertionType
): Promise<PartSearchData[]> => {
  return httpClient<PartSearchData[]>('/advancedBulkSearchSerials', HttpMethod.Post, data);
};

export const getSearchShipment = (data: string): Promise<ShipmentSearchData[]> => {
  return httpClient<ShipmentSearchData[]>(
    `/searchShipment?searchQuery=${encodeURIComponent(data)}`,
    HttpMethod.Get
  );
};

export const searchNodesPartsWithoutPartId = (name: string): Promise<LocationNodeData[]> => {
  const url = `/searchParts?name=${encodeURIComponent(name)}&includeSiblingNodes=true&categoryCode=operational`;
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};
export const searchLinkShipments = (data: LinkShipmentSearchData): Promise<LinkShipmentSearchResult[]> => {
  const url = `/linkShipmentSearch?id=${data.id}&clientHierarchy=${data.clientHierarchy}`;
  return httpClient<LinkShipmentSearchResult[]>(url, HttpMethod.Get);
};

export const advancePartSearch = (data: AdvancePartSearch): Promise<PartLiveSearchData[]> => {
  // support AM-PTID-, part type name, part type number search to get parts
  const searchableData = Array.isArray(data.searchableValues)
    ? data.searchableValues.join(',')
    : (data.searchableValues ?? '');
  const url = `/advancePartSearch?searchableData=${encodeURIComponent(searchableData)}`;
  return httpClient<PartLiveSearchData[]>(url, HttpMethod.Get);
};

export const advancePartSearchByName = (name: string): Promise<PartTypeLiveSearchData[]> => {
  const url = `/advanceSearchPartTypes?searchableData=${encodeURIComponent(name)}`;
  return httpClient<PartTypeLiveSearchData[]>(url, HttpMethod.Get);
};

export const searchAllNodesByName = (name: string): Promise<LiveSearchResponse[]> => {
  return httpClient<LiveSearchResponse[]>(`/searchByNodeName?name=${encodeURIComponent(name)}`, HttpMethod.Get);
};

// [categoryCode=operational || categoryCode=system and nodeTypeCode=sibling] data will recive within this route
export const searchAllocationNodes = (name: string): Promise<LocationNodeData[]> => {
  return httpClient<LocationNodeData[]>(`/searchAllocations?name=${encodeURIComponent(name)}`, HttpMethod.Get);
};

export const searchNodeParts = (data: SearchNodeParts): Promise<LocationNodeData[]> => {
  const url = `/searchNodeParts?name=${encodeURIComponent(data.name.trim())}&isInClientHierarchy=${data.isInClientHierarchy}&clientId=${data.clientId}`;
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const getSubLocationsForUpdateServices = (
  subLocationSearchData: SubLocationSearch
): Promise<LocationNodeData[]> => {
  return httpClient<LocationNodeData[]>(
    `/searchSubLocationsForUpdateServices/${subLocationSearchData.id}/name/${encodeURIComponent(subLocationSearchData.name ?? '')}`,
    HttpMethod.Get
  );
};

export const searchNodeByNameOrCode = (name: string): Promise<LocationNodeData[]> => {
  return httpClient<LocationNodeData[]>(
    `/searchnodes?name=${encodeURIComponent(name)}&hasCode=true`,
    HttpMethod.Get
  );
};

// Returns nodes which 'Can have parts' and parts. Supports AM-LID, AM-PID, serial1 and node name searches.
export const searchTlcNodesParts = (data: TlcNodesPartsSearchData): Promise<LocationNodeData[]> => {
  let url = `/searchTlcNodesParts?name=${encodeURIComponent(data.name)}&currentLocation=${data.currentLocation}&categoryCode=operational`;
  if (data.id !== undefined) {
    url += `&id=${data.id}`;
  }
  if (data.currentLocationType) {
    url += `&currentLocationType=${encodeURIComponent(data.currentLocationType)}`;
  }
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

// id -: either id can be shipmentId or nodeId
// recieveType -: [individual, bulk]
export const reciveShipmentsSearchNodes = (data: RecieveShipmentSearchNodes): Promise<LocationNodeData[]> => {
  return httpClient<LocationNodeData[]>(
    `/shipment/recieve/searchNodes/${data.id}/recieveType/${encodeURIComponent(data.recieveType)}?name=${encodeURIComponent(data.name)}&categoryCode=operational,system`,
    HttpMethod.Get
  );
};

export const searchNodeReceive = (data: SearchData): Promise<LocationNodeData[]> => {
  let url = `/searchnodes?name=${encodeURIComponent(data.name)}&categoryCode=operational&includeSiblings=true`;
  if (data?.hasCode) {
    // hasCode property allow AM-LID search when true
    url += '&hasCode=true';
  }
  return httpClient<LocationNodeData[]>(url, HttpMethod.Get);
};

export const batchRecieveBulkSearchSerials = (
  batchRecieveBulkLiveSearchData: BatchRecieveBulkLiveSearchData
): Promise<SerializedShipmentManifestData[]> => {
  const serials = Array.isArray(batchRecieveBulkLiveSearchData.serials)
    ? batchRecieveBulkLiveSearchData.serials.join(',')
    : (batchRecieveBulkLiveSearchData.serials ?? '');
  return httpClient<SerializedShipmentManifestData[]>(
    `/shipment/manifest/serialized/nodes/${batchRecieveBulkLiveSearchData.nodeId}/bulk/serials?serials=${encodeURIComponent(serials)}`,
    HttpMethod.Get,
    batchRecieveBulkLiveSearchData
  );
};

export const searchDesiredParentsForSerial = (data: DesiredSerialParentData): Promise<DesiredSerialParentDataResponse[]> => {
  let url = `/searchDesiredParentsForSerial?partTypeId=${data.partTypeId}&clientContractIds=${encodeURIComponent(JSON.stringify(data.clientContractIds))}&locationId=${data.locationId}`;
  if (data.serial) {
    url += `&serial=${encodeURIComponent(data.serial)}`;
  }
  return httpClient<DesiredSerialParentDataResponse[]>(url, HttpMethod.Get);
};
